import {useEffect} from 'react'

import {Span, StartSpanOptions} from '@sentry/core'
import * as Sentry from '@sentry/react'
import {createRoutesFromChildren, matchRoutes, useLocation, useNavigationType} from 'react-router-dom'

import {InitializeParameters} from './interfaces'

const SENTRY_TAG = {
    TYPE: 'type',
} as const

const SENTRY_TAG_TYPE = {
    GENERAL: 'general',
    ERROR_BOUNDARY: 'error-boundary',
    MESSAGE: 'message',
} as const

/**
 * Transaction 샘플링 비율
 * - 개발환경에서는 전체 트랜잭션을 샘플링하도록 합니다.
 */
const TRACES_SAMPLE_RATE = (process.env.REACT_APP_PROFILE || process.env.NEXT_PUBLIC_PROFILE) === 'real' ? 0.2 : 1

/**
 * 서버 구동 환경
 */
const ENVIRONMENT = process.env.REACT_APP_PROFILE ?? process.env.NEXT_PUBLIC_PROFILE ?? 'dev'

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN || process.env.NEXT_PUBLIC_SENTRY_DSN

/**
 * Sentry Dev Mode 여부
 * - 환경 변수 `REACT_APP_IS_SENTRY_DEV_MODE` 를 'true' 로 설정하면, 로컬 환경용으로 동작합니다.
 * - 로그 출력이 활성화됩니다.
 */
export const isDevMode = process.env.REACT_APP_IS_SENTRY_DEV_MODE === 'true'

/**
 * Sentry로 에러를 전송하는 함수를 생성합니다.
 *
 * @param tagType - Sentry 태그 타입 (SENTRY_TAG_TYPE 상수 중 하나)
 * @returns 에러와 추가 정보를 Sentry로 전송하는 함수
 *          반환된 함수는 다음과 같은 매개변수를 받습니다:
 *          - error: 캡처할 Error 객체
 *          - extra: (선택 사항) Sentry에 추가로 전송할 데이터
 */
export const createSentryErrorReporter =
    (tagType: Constant<typeof SENTRY_TAG_TYPE>, level: Sentry.SeverityLevel = 'error') =>
    (error: Error, extra?: unknown) => {
        Sentry.withScope((scope) => {
            scope.setTag(SENTRY_TAG.TYPE, tagType)
            scope.setExtra('error', error)
            extra && scope.setExtra('extra', extra)
            scope.setLevel(level)
            Sentry.captureException(error)
        })
    }

/** 일반 에러 수집 */
export const sendGeneralError = createSentryErrorReporter(SENTRY_TAG_TYPE.GENERAL)

/** 참고 수준 에러 기록 */
export const sendInfoError = createSentryErrorReporter(SENTRY_TAG_TYPE.GENERAL, 'info')

/** 주의 수준 에러 기록 */
export const sendWarningError = createSentryErrorReporter(SENTRY_TAG_TYPE.GENERAL, 'warning')

/** ErrorBoundary Error 수집 */
export const sendErrorBoundary = createSentryErrorReporter(SENTRY_TAG_TYPE.ERROR_BOUNDARY, 'fatal')

/** Sentry 메시지 전송 */
export function sendMessage(message: string, extras?: Record<string, unknown>) {
    Sentry.withScope((scope) => {
        scope.setTag(SENTRY_TAG.TYPE, SENTRY_TAG_TYPE.MESSAGE)
        extras && scope.setExtras(extras)
        Sentry.captureMessage(message)
    })
}

/** Transaction 시작 */
export function startTransaction(spanOption: StartSpanOptions) {
    return Sentry.startSpan(spanOption, (span) => span)
}

export function setSpan(
    spanOption: StartSpanOptions,
    callback: (span: Span) => void = () => {
        /* empty */
    },
) {
    Sentry.withScope(() => {
        Sentry.startSpan(spanOption, callback)
    })
}

/** Sentry 태그를 설정 */
export const setTags = Sentry.setTags

export const setTransactionName = (transactionName: string) =>
    Sentry.getCurrentScope().setTransactionName(transactionName)

export const withProfiler = Sentry.withProfiler

/**
 * 현재 활성화된 Root Span(구: Transaction)의 이름을 변경합니다.
 * - Transaction/Span 이 혼용되어 사용되된 부분을 Span 으로 통일되었으나, 기존 용어와의 통합을 위해 Transaction 이란 이름을 계속 사용합니다.
 * @param name - 변경할 Root Transaction(Span) 이름
 */
export function setRootTransactionName(name: string) {
    const activeSpan = Sentry.getActiveSpan()
    const rootSpan = activeSpan && Sentry.getRootSpan(activeSpan)
    rootSpan && Sentry.updateSpanName(rootSpan, name)
}

/**
 * Sentry 초기화
 * - 실행시점에 package.json을 바탕으로 release 정보를 생성하여 전송합니다.
 */
export function initialize({
    tags,
    transactionName,
    tracingOrigins: allowUrls = [/.*/],
    normalizeDepth,
}: InitializeParameters) {
    if (!SENTRY_DSN) {
        // eslint-disable-next-line no-console
        console.error('Sentry 초기화 실패. REACT_APP_SENTRY_DSN 환경변수를 확인하세요.')
        return
    }

    Sentry.init({
        // pageload, navigate 이벤트 발생 시 실행됩니다.
        beforeSendTransaction(event) {
            event.transaction = transactionName
            return event
        },
        dsn: SENTRY_DSN,
        integrations: [],
        environment: ENVIRONMENT,
        tracesSampleRate: TRACES_SAMPLE_RATE,
        normalizeDepth,
        allowUrls,
        // ex) ordersheet@24.11.27+AAAAAAA
        release: `${process.env.REACT_APP_PACKAGE_NAME || process.env.NEXT_PUBLIC_PACKAGE_NAME}@${process.env.REACT_APP_PACKAGE_VERSION || process.env.NEXT_PUBLIC_PACKAGE_VERSION}+${process.env.REACT_APP_TAG_NAME || process.env.NEXT_PUBLIC_TAG_NAME}`,
        debug: isDevMode,
    })
    tags && Sentry.setTags(tags)

    /**
     * Lazy Loading Integrations
     * - 초기 번들사이즈 감소 기대
     *
     * @see https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/#lazy-loading-integrations
     */
    import('@sentry/react').then((lazyLoadedSentry) => {
        ;[
            lazyLoadedSentry.reactRouterV6BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
            Sentry.breadcrumbsIntegration({console: false}),
        ].forEach(Sentry.addIntegration)
    })
}
